import { useMemo } from 'react';

import { format } from 'date-fns';

import { useDateLocalize } from './locales/useDateLocalize.js';

/**
 * Formatting options for time values.
 */ type TimeFormatOptions = {
  /**
   * The format to use for the time.
   *
   * Examples:
   *
   * | Format | en-US | en-GB |
   * |--------|--------|--------|
   * | `p`    | 11:00 PM | 23:00 |
   * | `pp`   | 11:00:00 PM | 23:00:00 |
   * | `ppp`  | 11:00:00 PM GMT+2 | 23:00:00 GMT+2 |
   * | `pppp` | 11:00:00 PM GMT+02:00 | 23:00:00 GMT+02:00 |
   */
  format?: 'p' | 'pp' | 'ppp' | 'pppp';
};

type Params = {
  options?: TimeFormatOptions;
};

/**
 * Returns a localized time formatter.
 */
export const useTimeFormatter = (params?: Params) => {
  const locale = useDateLocalize();
  const timeFormat = params?.options?.format ?? 'p';

  return useMemo(
    () => ({
      format: (date: number | string | Date) =>
        format(new Date(date), timeFormat, {
          locale,
        }),
    }),
    [timeFormat, locale],
  );
};
